import { getCustomerSuccess } from '@essent/customer';
import type { FormAddress } from '@innogy/common-ui/forms';
import {
  convertCorrespondenceAddressToFormAddress,
  createAddressFormState,
  getAddressValidator,
} from '@innogy/common-ui/forms';
import { emobilityActions } from '@innogy/emobility-shared';
import {
  createProgressiveFormGroupState,
  createProgressiveNgrxFormReducerWithFormStateUpdate,
  type ProgressiveFormStateImplementor,
} from '@innogy/progressive-ngrx-forms';
import type { PrefillAction } from '@innogy/shared/forms';
import { validateSequential } from '@innogy/utils-deprecated';
import type { Action } from '@ngrx/store';
import { on } from '@ngrx/store';
import { setValue, updateGroup } from 'ngrx-forms';

export const installationAddressFormId = 'installation-address';
export const installationAddressGroupId = `${installationAddressFormId}.installationAddressState`;
export const correspondenceAddressGroupId = `${installationAddressFormId}.correspondenceAddressState`;

export interface LeadAddressState {
  installationAddressState: FormAddress;
  divergentCorrespondenceAddress: boolean;
  correspondenceAddressState: FormAddress;
}

export interface LeadAddressFormState
  extends ProgressiveFormStateImplementor<LeadAddressState> {}

export interface AddressReducerOptions {
  prefillOnAction?: PrefillAction<FormAddress>;
  submitPrefilledFormIfValid?: boolean;
}

export const getInitialLeadFormAddressState = (
  installationAddressFormState: ReturnType<typeof createAddressFormState>,
  correspondenceAddressState: ReturnType<typeof createAddressFormState>
): LeadAddressFormState => ({
  progressiveForm: createProgressiveFormGroupState(installationAddressFormId, {
    installationAddressState: installationAddressFormState.initialAddressState,
    divergentCorrespondenceAddress: false,
    correspondenceAddressState: correspondenceAddressState.initialAddressState,
  }),
});

export const validateLeadFormAddressForm = (state: LeadAddressFormState) =>
  updateGroup<LeadAddressState>({
    installationAddressState: updateGroup<FormAddress>(
      getAddressValidator(
        state.progressiveForm.formState.controls.installationAddressState
      )
    ),
    correspondenceAddressState: updateGroup<FormAddress>(
      state.progressiveForm.formState.value.divergentCorrespondenceAddress
        ? getAddressValidator(
            state.progressiveForm.formState.controls.correspondenceAddressState
          )
        : {
            postalCode: validateSequential(),
            addressType: validateSequential(),
            city: validateSequential(),
            communicationNumber: validateSequential(),
            countryCode: validateSequential(),
            houseNumberAddition: validateSequential(),
            street: validateSequential(),
          }
    ),
  })(state.progressiveForm.formState);

const _reducer = (
  installationAddressFormState: ReturnType<typeof createAddressFormState>,
  correspondenceAddressState: ReturnType<typeof createAddressFormState>
) =>
  createProgressiveNgrxFormReducerWithFormStateUpdate(
    getInitialLeadFormAddressState(
      installationAddressFormState,
      correspondenceAddressState
    ),
    validateLeadFormAddressForm,
    on(getCustomerSuccess, (state, action) => ({
      ...state,
      progressiveForm: {
        ...state.progressiveForm,
        formState: setValue<LeadAddressState>({
          ...state.progressiveForm.formState.value,
          installationAddressState: {
            ...state.progressiveForm.formState.value.installationAddressState,
            ...convertCorrespondenceAddressToFormAddress(
              action.payload.correspondence?.address,
              false
            ),
          },
        })(state.progressiveForm.formState),
      },
    })),
    on(
      emobilityActions.getSalesPartnerProspectV1.successAction,
      (state, action) => ({
        ...state,
        progressiveForm: {
          ...state.progressiveForm,
          formState: setValue<LeadAddressState>({
            ...state.progressiveForm.formState.value,
            installationAddressState: {
              ...state.progressiveForm.formState.value.installationAddressState,
              ...convertCorrespondenceAddressToFormAddress(
                {
                  street: action.payload.installationAddress?.street ?? '',
                  city: action.payload.installationAddress?.city ?? '',
                  postcode:
                    action.payload.installationAddress?.postalCode ?? '',
                  houseNumber:
                    action.payload.installationAddress?.houseNumber ?? '',
                  houseNumberExtension:
                    action.payload.installationAddress?.houseNumberAddition ??
                    '',
                  countryCode: '',
                },
                false
              ),
            },
          })(state.progressiveForm.formState),
        },
      })
    ),
    installationAddressFormState.onAddressFormAction(
      (state) =>
        state.progressiveForm.formState.controls.installationAddressState
    ),
    correspondenceAddressState.onAddressFormAction(
      (state) =>
        state.progressiveForm.formState.controls.correspondenceAddressState
    )
  );

export function createLeadFormAddressReducer(formId: string) {
  const installationAddressFormState = createAddressFormState(formId);
  const correspondenceAddressFormState = createAddressFormState(formId);

  const reducer = _reducer(
    installationAddressFormState,
    correspondenceAddressFormState
  );

  return (
    state = getInitialLeadFormAddressState(
      installationAddressFormState,
      correspondenceAddressFormState
    ),
    action: Action
  ): LeadAddressFormState => {
    return reducer(state, action);
  };
}
